<template>
  <div class="p-3">
    <div class="form-info">
      <div class="info-item">
        <span class="label-style">
          <i class="el-icon-document label-icon"></i>
          Form Name:
        </span>
        <span class="value-style">{{ formbuilderDetails.name }}</span>
      </div>
      <div class="info-item" v-if="formattedLogs.length">
        <span class="label-style">
          <i class="el-icon-user label-icon"></i>
          Initiated By:
        </span>
        <span class="value-style">{{ formattedLogs[0].name }}</span>
      </div>
      <div class="info-item" v-if="formattedLogs.length">
        <span class="label-style">
          <i class="el-icon-date label-icon"></i>
          Initiated Date:
        </span>
        <span class="value-style">{{ formattedLogs[0].dateTime | globalDateTimeFormat }}</span>
      </div>
      <div class="info-item">
        <span class="label-style">
          <i class="el-icon-tickets label-icon"></i>
          Form ID:
        </span>
        <span class="value-style">{{ currentFormbuilderData.formbuilderdata_unique_id }}</span>
      </div>
      <div class="info-item" v-if="currentFormbuilderData.approval_status">
        <span class="label-style">
          <i class="el-icon-refresh"></i>
          Current Status:
        </span>
        <span class="value-style" v-if="currentFormbuilderData.approval_status == 'PENDING'">
          Pending
        </span>
        <span class="value-style" v-else-if="currentFormbuilderData.approval_status == 'APPROVED'">
          Approved
        </span>
        <span class="value-style" v-else-if="currentFormbuilderData.approval_status == 'REJECTED'">
          Rejected
        </span>
      </div>
    </div>

    <el-table :data="formattedLogs" style="width: 100%" size="small" class="mt-1">
      <el-table-column label="#" type="index" width="40" />

      <el-table-column label="WF sequence" width="220">
        <template slot-scope="scope">
          {{ getSequenceDescription(scope.row) }}
          <p v-if="scope.row.action == 'USER SUBMITTED'">[{{ scope.row.email }}]</p>
          <p v-else-if="scope.row.action == 'APPROVED'">[{{ scope.row.approved_by }}]</p>
          <p v-else-if="scope.row.action == 'REJECTED'">[{{ scope.row.rejected_by }}]</p>
        </template>
      </el-table-column>

      <el-table-column label="Role" prop="role" width="100">
        <template slot-scope="scope">
          <span style="color: #555555">{{ scope.row.role }}</span>
        </template>
      </el-table-column>

      <el-table-column label="WF Level" width="80">
        <template slot-scope="scope">Level {{ scope.row.level + 1 }}</template>
      </el-table-column>

      <el-table-column label="WF Action">
        <template slot-scope="scope">
          <template v-if="['APPROVAL PENDING', 'APPROVED', 'REJECTED'].includes(scope.row.action)">
            <el-popover
              placement="top-start"
              :title="getPopOverTitle(scope.row)"
              width="300"
              trigger="hover"
            >
              <!-- APPROVAL PENDING content -->
              <div v-if="scope.row.action == 'APPROVAL PENDING'" class="approval-pending compact">
                <div
                  v-for="(approver, index) in getAssignedApproversByLevel(scope.row.level)"
                  :key="index"
                  class="approver-item"
                >
                  <div class="approver-name">
                    <i class="el-icon-user"></i>
                    <span class="approver-text">{{ approver.name }}</span>
                  </div>
                  <div class="approver-email">
                    {{ approver.email }}
                  </div>
                  <div class="approver-timestamp">
                    <i class="el-icon-time"></i>
                    <span class="timestamp-text">
                      Mail sent at {{ approver.sent_at | globalDateTimeFormat }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-else-if="scope.row.action == 'APPROVED'" class="approval-info compact">
                <div class="approval-item">
                  <i class="el-icon-user-solid icon-style"></i>
                  <span class="label">Approved By:</span>
                  <span class="value">{{ scope.row.approved_by }}</span>
                </div>
                <div class="approval-item">
                  <i class="el-icon-time icon-style"></i>
                  <span class="label">Approved At:</span>
                  <span class="value">{{ scope.row.dateTime | globalDateTimeFormat }}</span>
                </div>
                <div class="approval-item" v-if="scope.row.approved_reason">
                  <i class="el-icon-success icon-style"></i>
                  <span class="label">Approved Reason:</span>
                  <span class="value">{{ scope.row.approved_reason }}</span>
                </div>
              </div>

              <div v-else-if="scope.row.action == 'REJECTED'" class="rejection-info compact">
                <div class="rejection-item">
                  <i class="el-icon-user-solid icon-style"></i>
                  <span class="label">Rejected By:</span>
                  <span class="value">{{ scope.row.rejected_by }}</span>
                </div>
                <div class="rejection-item">
                  <i class="el-icon-time icon-style"></i>
                  <span class="label">Rejected At:</span>
                  <span class="value">{{ scope.row.dateTime | globalDateTimeFormat }}</span>
                </div>
                <div class="rejection-item">
                  <i class="el-icon-warning icon-style"></i>
                  <span class="label">Rejected Reason:</span>
                  <span class="value">{{ scope.row.rejected_reason }}</span>
                </div>
              </div>

              <span slot="reference" style="display: inline-flex; align-items: center">
                <i :class="getActionClass(scope.row.action).iconClass"></i>
                <span
                  class="inline-block ml-0.5"
                  :class="getActionClass(scope.row.action).textClass"
                >
                  {{ getActionClass(scope.row.action).label }}
                </span>
              </span>
            </el-popover>
          </template>

          <template v-else>
            <span style="display: inline-flex; align-items: center">
              <i :class="getActionClass(scope.row.action).iconClass"></i>
              <span class="inline-block ml-0.5" :class="getActionClass(scope.row.action).textClass">
                {{ getActionClass(scope.row.action).label }}
              </span>
            </span>
          </template>
        </template>
      </el-table-column>

      <el-table-column label="Date & Time" prop="dateTime">
        <template slot-scope="scope">
          {{ scope.row.dateTime | globalDateTimeFormat }}
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>
<script>
  export default {
    props: ['currentFormbuilderData', 'formbuilderDetails'],
    data() {
      return {
        logs: [],
        users: [],
        entityNameObj: {},
        allCompanyUserTypesDataObj: {},
        loading: false,
        selectedUserIndex: null,
        showFormDataLogs: false,
      };
    },
    computed: {
      formattedLogs() {
        let rawLogs = this.currentFormbuilderData?.advanced_logs?.length
          ? this.currentFormbuilderData.advanced_logs
          : [];

        let normalizedLogs = rawLogs.map((log) => {
          return {
            level:
              log.level_index != null
                ? log.level_index
                : log.levelIndex != null
                  ? log.levelIndex
                  : null,
            role: (log.role || '').toString().toUpperCase(),
            action: log.action,
            timestamp:
              log.submitted_at || log.approved_at || log.rejected_at || log.timestamp || null,
            name: log.name || log.user_name || '',
            email: log.email || '',
            to_level: log?.to_level ?? null,
            isFake: false,
            approvers: log.approvers || [],
            approved_by: log.approved_by || null,
            rejected_by: log.rejected_by || null,
            rejected_reason: log.rejected_reason || null,
            approved_reason: log.approved_reason || null,
          };
        });

        let skippedDirectiveLog = normalizedLogs.find(
          (log) => log.action === 'SKIPPED' && log.to_level != null
        );
        let skippedTimestamp = skippedDirectiveLog?.timestamp ?? null;

        normalizedLogs = normalizedLogs.filter(
          (log) => !(log.action === 'SKIPPED' && log.to_level != null)
        );

        normalizedLogs.sort((a, b) => (a.level ?? 0) - (b.level ?? 0));

        // Fill in fake logs for skipped levels
        let filledLogs = [];
        for (let i = 0; i < normalizedLogs.length; i++) {
          let current = normalizedLogs[i];
          let prev = normalizedLogs[i - 1];

          filledLogs.push(current);

          if (i > 0 && prev.level != null && current.level != null) {
            let gap = current.level - prev.level;
            if (gap > 1) {
              for (let skipped = prev.level + 1; skipped < current.level; skipped++) {
                filledLogs.push({
                  level: skipped,
                  role: this.getRoleByLevel(skipped),
                  action: 'SKIPPED',
                  timestamp: skippedTimestamp,
                  name: '',
                  email: '',
                  isFake: true,
                  to_level: null,
                  approvers: [],
                  approved_by: null,
                  rejected_by: null,
                });
              }
            }
          }
        }

        filledLogs.sort((a, b) => (a.level ?? 0) - (b.level ?? 0));

        return filledLogs.map((log) => {
          let formattedLog = {
            role: log.role,
            level: log.level,
            action: log.action,
            dateTime: this.getDateTime(log),
            name: log.name,
            email: log.email,
            isFake: log.isFake || false,
            to_level: log.to_level ?? null,
          };

          if (log.action === 'APPROVAL PENDING') {
            formattedLog.approvers = log.approvers;
          }

          if (log.action === 'APPROVED') {
            formattedLog.approved_by = log.approved_by;
            formattedLog.approved_reason = log.approved_reason;
          }

          if (log.action === 'REJECTED') {
            formattedLog.rejected_by = log.rejected_by;
            formattedLog.rejected_reason = log.rejected_reason;
          }

          return formattedLog;
        });
      },
    },
    watch: {},
    mounted() {},
    methods: {
      getPopOverTitle(rowData) {
        if (rowData.action == 'APPROVAL PENDING') {
          return 'Assigned Approvers:';
        } else if (rowData.action == 'APPROVED') {
          return 'Approver Details:';
        } else if (rowData.action == 'REJECTED') {
          return 'Approver Details:';
        } else {
          return '';
        }
      },
      getAssignedApproversByLevel(index) {
        let level = this.currentFormbuilderData.advanced_approval_users.find(
          (e) => e.current_level_index == index
        );
        return level.approval_users;
      },
      getActionClass(action) {
        let map = {
          'USER SUBMITTED': {
            iconClass: 'el-icon-document primary',
            textClass: 'primary',
            label: 'User Submitted',
          },
          'APPROVAL PENDING': {
            iconClass: 'el-icon-time warning',
            textClass: 'warning',
            label: 'Approval Pending',
          },
          APPROVED: {
            iconClass: 'el-icon-success success',
            textClass: 'success',
            label: 'Approved',
          },
          'AUTO APPROVED': {
            iconClass: 'el-icon-success success',
            textClass: 'success',
            label: 'Auto Approved',
          },
          REJECTED: {
            iconClass: 'el-icon-error error',
            textClass: 'error',
            label: 'Rejected',
          },
          AUTO_REJECTED: {
            iconClass: 'el-icon-error error',
            textClass: 'error',
            label: 'Auto Rejected',
          },
          'AUTO REJECTED': {
            iconClass: 'el-icon-error error',
            textClass: 'error',
            label: 'Auto Rejected',
          },
          SKIPPED: {
            iconClass: 'el-icon-remove info',
            textClass: 'skipped',
            label: 'Skipped',
          },
        };

        return (
          map[action] || {
            iconClass: '',
            textClass: '',
            label: action,
          }
        );
      },
      getRoleByLevel(levelIndex) {
        return this.formbuilderDetails.advanced_approval_users[levelIndex].role.toUpperCase();
      },
      getApproverType(rowData) {
        if (rowData.role == 'APPROVER') {
          let levelData = this.formbuilderDetails.advanced_approval_users[rowData.level];
          return levelData.approver_type;
        } else if (rowData.role == 'USER' && rowData.action == 'USER SUBMITTED') {
          return rowData.name;
        }
      },
      getSequenceDescription(rowData) {
        if (rowData.role == 'USER' && rowData.action == 'USER SUBMITTED') {
          return rowData.name;
        } else if (rowData.role == 'APPROVER') {
          return this.getApproverTypeByLevelIndex(rowData.level);
        }
      },
      getApproverTypeByLevelIndex(index) {
        let type = this.formbuilderDetails.advanced_approval_users[index].approver_type;
        if (type == 'constant_approver' || type == 'dynamic_attribute_based') {
          type = type.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
        }
        return type;
      },

      getDateTime(log) {
        return log.submitted_at || log.approved_at || log.rejected_at || log.timestamp || '';
      },
      openAdvancedLogs() {
        this.showFormDataLogs = true;
      },
      closeFormDataLogs() {
        this.showFormDataLogs = false;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .form-info {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    background: #f9f9f9;
    padding: 12px 16px;
    border-radius: 6px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    box-shadow: 0 1px 4px rgb(0 0 0 / 0.08);
    color: #333;
    font-size: 0.9rem;
    margin-bottom: 10px;
  }

  .info-item {
    display: flex;
    flex-direction: column;
  }

  .label-style {
    font-weight: 600;
    color: #666;
    font-size: 0.75rem;
    margin-bottom: 2px;
    text-transform: none;
    letter-spacing: 0.02em;
  }

  .value-style {
    font-size: 0.9rem;
    font-weight: 500;
    color: #222;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rejection-info.compact {
    font-size: 12px;
    color: #444;
    line-height: 1.5;
    padding: 8px;
    background-color: #fdf6f6;
    border-left: 2px solid #e74c3c;
    border-radius: 3px;
    margin: 6px 0;
    font-family: 'Segoe UI', Arial, sans-serif;
  }

  .rejection-info.compact .rejection-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 6px;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }

  .rejection-info.compact .rejection-item:last-child {
    margin-bottom: 0;
  }

  .rejection-info.compact .icon-style {
    color: #e74c3c;
    margin-right: 6px;
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 2px;
  }
  .rejection-info.compact .label {
    font-weight: 600;
    color: #666;
    min-width: 100px;
    margin-right: 3px;
    font-size: 11px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .rejection-info.compact .value {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: normal;
    hyphens: auto;
    color: #444;
    font-size: 11.5px;
    flex: 1;
    display: block;
  }

  .approval-info.compact {
    font-size: 12px;
    color: #333;
    line-height: 1.5;
    padding: 8px;
    background-color: #f6fdf6;
    border-left: 2px solid #27ae60;
    border-radius: 3px;
    margin: 6px 0;
    font-family: 'Segoe UI', Arial, sans-serif;
  }

  .approval-info.compact .approval-item {
    display: flex;
    align-items: center;
    margin-bottom: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .approval-info.compact .approval-item:last-child {
    margin-bottom: 0;
  }

  .approval-info.compact .icon-style {
    color: #27ae60;
    margin-right: 6px;
    font-size: 14px;
  }

  .approval-info.compact .label {
    font-weight: 600;
    color: #555;
    min-width: 76px;
    margin-right: 6px;
    font-size: 11px;
  }

  .approval-info.compact .value {
    color: #333;
    flex: 1;
    font-size: 11.5px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .approval-pending.compact {
    font-size: 12px;
    color: #444;
    line-height: 1.5;
    padding: 8px 10px;
    background-color: #f8f9ff;
    border-left: 2px solid #409eff;
    border-radius: 3px;
    margin: 6px 0;
    font-family: 'Segoe UI', Arial, sans-serif;
  }

  .approval-pending.compact .approver-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 6px;
  }

  .approval-pending.compact .approver-item:last-child {
    margin-bottom: 0;
  }

  .approval-pending.compact .approver-name {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #2c3e50;
    font-size: 12px;
  }

  .approval-pending.compact .approver-name i {
    color: #409eff;
    margin-right: 6px;
    font-size: 14px;
  }

  .approval-pending.compact .approver-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .approval-pending.compact .approver-email {
    font-size: 11.5px;
    color: #555;
    margin-left: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .approval-pending.compact .approver-timestamp {
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #777;
    margin-left: 20px;
  }

  .approval-pending.compact .approver-timestamp i {
    color: #bbb;
    margin-right: 5px;
    font-size: 13px;
  }

  .approval-pending.compact .timestamp-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .role-styling {
    color: #334155;
    font-weight: 500;
  }
</style>
